/* istanbul ignore file  */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Snackbar } from "@mui/material";
import { useParticipantById } from "@/hooks/useParticipants";
import { copyToClipboard } from "@/utils/copyToClipboard";
import { usePageStore } from "@/hooks/usePageStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import ProviderModulesModal from "@/ui/modals/clients/ProviderModulesModal";
import ProfileBreadcrumb from "@/ui/breadcrumbs/clients/ProfileBreadcrumb";
import ProfileCards from "@/ui/content/clients/ProfileCards";
import { ParticipantResponse } from "@/domain/participant.interface";
import { RoleSubType } from "@/domain/enrollment.interface";
import ClientEditModal from "@/ui/modals/clients/ClientEditModal";
import GenericAlertModal from "@/components/AlertModal";
import { useAppTheme } from "@/utils/theme";
import { useDismissedClient } from "@/hooks/useClients";
import { ErrorResponse } from "@/domain/error.interface";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import ActionCards from "@/ui/content/clients/ActionCards";
import {
  useGetEnrollmentBalance,
  useSearchJournals,
} from "@/hooks/useEnrollments";
import { useGetAlertsByOfficerId } from "@/hooks/useAlerts";
import CasePlanModal from "@/ui/modals/clients/CasePlanModal";
import ManualReimbursementModal from "@/ui/modals/clients/ManualReimbursementModal";
import { AppointmentsCard } from "@/components/appointments";

const ClientProfile: React.FC = () => {
  const { t } = useTranslation("clients");
  const navigate = useNavigate();
  const theme = useAppTheme();
  const {
    userInfo,
    modules,
    fetchModules,
    hasSystemAdminRole,
    hasStandardAccountRole,
  } = useUserStore();
  const { participantId } = useParams();
  const { showSnackBar } = useFeedBack();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { setPageTitle } = usePageStore();
  const {
    data: participantResponse,
    isLoadingParticipant,
    refetchParticipant,
  } = useParticipantById(participantId || "");

  const { enrollmentBalance, refetchEnrollmentBalance } =
    useGetEnrollmentBalance(
      participantResponse?.lastEnrollment?._id ?? "",
      !!participantResponse?.lastEnrollment
    );
  const { journals } = useSearchJournals({
    sortField: "createdAt",
    sortOrder: -1,
    pageNumber: 0,
    pageSize: 200,
    enrollmentId: participantResponse?.lastEnrollment?._id ?? "",
  });

  const { alerts, isLoadingAlerts } = useGetAlertsByOfficerId(
    userInfo?._id || "",
    100,
    1
  );

  const createAuditLog = useCreateAuditlog();
  const dismissClient = useDismissedClient();

  const [genericAlert, setGenericAlert] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const [isSupportStaffModalOpen, setIsSupportStaffModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDemoUploadModalOpen, setIsDemoUploadModalOpen] = useState(false);
  const [isManualReimbursementModalOpen, setIsManualReimbursementModalOpen] =
    useState(false);

  const handleOpenSupportStaffModal = () => setIsSupportStaffModalOpen(true);
  const handleCloseSupportStaffModal = () => setIsSupportStaffModalOpen(false);

  const handleOpenManualReimbursementModal = () =>
    setIsManualReimbursementModalOpen(true);
  const handleCloseManualReimbursementModal = () =>
    setIsManualReimbursementModalOpen(false);

  const handleOpenEditModal = () => setIsEditModalOpen(true);
  const handleCloseEditModal = () => setIsEditModalOpen(false);
  const handleCloseDemoUploadModal = () => setIsDemoUploadModalOpen(false);

  const handleOpenDismissModal = () => {
    setGenericAlert({
      open: true,
      title: "Disenroll",
      message:
        "Are you sure you want to disenroll this client? This action cannot be undone.",
      onConfirm: handleDismissClient,
    });
  };
  const handleCloseDismissModal = () => {
    setGenericAlert({
      open: false,
      title: "",
      message: "",
      onConfirm: () => {},
    });

    navigate("/participants");
  };

  const handleDismissClient = async () => {
    try {
      await dismissClient.mutateAsync(
        {
          participantId: participantResponse?.participant._id as string,
          enrollmentId: participantResponse?.lastEnrollment?._id as string,
          updatedBy: userInfo?._id as string,
        },
        {
          onSuccess: () => {
            showSnackBar(
              "The client has been disenrolled and the user has been disabled.",
              "success"
            );
            refetchParticipant();
            handleCloseDismissModal();

            createAuditLog.mutate({
              appType: "WEB_BACK_OFFICE",
              module: MODULE_NAME.PARTICIPANTS_LIST,
              option: OPTIONS.PARTICIPANT_DISMISS,
              actionCode: ACTION_CODE.WEB_PART_DISMISS,
              action: "When dimissing client",
              detail: `Dismissed client ${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName} from the program`,
              transactionDate: new Date(),
              accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
              createdBy: userInfo?._id as string,
              targetId: participantResponse?.participant._id as string,
              programId: participantResponse?.lastEnrollment
                ?.programId as string,
              orgUnitId: participantResponse?.lastEnrollment
                ?.orgUnitId as string,
            });
          },
          onError: (error: ErrorResponse) => {
            showSnackBar(
              (Array.isArray(error.message)
                ? error.message[0]
                : error.message) ||
                error.error ||
                "An error occurred"
            );
          },
        }
      );
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleCopy = (text: string, message: string) => {
    copyToClipboard(text);
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  };

  const handleSnackbarClose = () => setIsSnackbarOpen(false);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.PARTICIPANTS_LIST,
    option: OPTIONS.PARTICIPANT_PROFILE,
    actionCode: ACTION_CODE.WEB_PART_PROFILE_VIEW,
    action: "When entering",
    detail: participantResponse?.participant
      ? `Viewed the client profile ${participantResponse.participant.firstName} ${participantResponse.participant.lastName}`
      : "",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    targetId: participantResponse?.participant._id as string,
    programId: participantResponse?.lastEnrollment?.programId as string,
    orgUnitId: participantResponse?.lastEnrollment?.orgUnitId as string,
  });

  useEffect(() => {
    if (participantResponse?.participant?.firstName) {
      setPageTitle(t("client_profile.page_title"));
    }
  }, []);

  useEffect(() => {
    if (hasSystemAdminRole || hasStandardAccountRole) return;

    const supportStaffIds =
      participantResponse?.lastEnrollment?.supportStaffsInfo?.map(
        (staff) => staff._id
      ) || [];
    const userId = userInfo?._id;

    if (userId && participantResponse) {
      if (!supportStaffIds.includes(userId)) {
        navigate("/401");
      }
    }
  }, [
    participantResponse,
    userInfo?._id,
    hasSystemAdminRole,
    hasStandardAccountRole,
    navigate,
  ]);

  useEffect(() => {
    if (!modules) {
      fetchModules();
    }
  }, [modules, fetchModules]);

  const createAuditLogAddStaff = async (
    staffNames: string[],
    roleType: RoleSubType,
    participantId?: string
  ) => {
    try {
      const staffString = staffNames.join(", ");
      const rolePlural = staffNames.length > 1 ? "s" : "";
      const auditLogDetails =
        roleType === RoleSubType.SUPPORT_STAFF
          ? {
              module: MODULE_NAME.PARTICIPANTS_MANAGE_SUPPORT_STAFF,
              option: OPTIONS.MANAGE_SUPPORT_STAFF,
              actionCode: ACTION_CODE.WEB_PART_ADDED_STAFF,
              action: "When added new staff",
              detail: `Assigned support staff${rolePlural} ${staffString} to ${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`,
            }
          : {
              module: MODULE_NAME.PARTICIPANTS_MANAGE_INTERNAL_PROVIDER,
              option: OPTIONS.MANAGE_INTERNAL_PROVIDERS,
              actionCode: ACTION_CODE.WEB_PART_ADDED_INT_PROVIDERS,
              action: "When added new internal providers",
              detail: `Assigned internal provider${rolePlural} ${staffString} to ${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`,
            };

      await createAuditLog.mutateAsync({
        appType: "WEB_BACK_OFFICE",
        module: auditLogDetails.module,
        option: auditLogDetails.option,
        actionCode: auditLogDetails.actionCode,
        action: auditLogDetails.action,
        detail: auditLogDetails.detail,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        targetId: participantId,
        programId: participantResponse?.lastEnrollment?.programId as string,
        orgUnitId: participantResponse?.lastEnrollment?.orgUnitId as string,
      });
    } catch (error) {
      showSnackBar(
        `An error occurred while adding staff auditlog: ${error}`,
        "error"
      );
    }
  };

  const createAuditLogRemoveStaff = async (
    staffName: string,
    roleType: RoleSubType,
    participantId?: string
  ) => {
    const auditLogDetails =
      roleType === RoleSubType.SUPPORT_STAFF
        ? {
            module: MODULE_NAME.PARTICIPANTS_MANAGE_SUPPORT_STAFF,
            option: OPTIONS.MANAGE_SUPPORT_STAFF,
            actionCode: ACTION_CODE.WEB_PART_REMOVED_STAFF,
            action: "When support staff removed",
            detail: `Removed support staff ${staffName} from ${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`,
          }
        : {
            module: MODULE_NAME.PARTICIPANTS_MANAGE_INTERNAL_PROVIDER,
            option: OPTIONS.MANAGE_INTERNAL_PROVIDERS,
            actionCode: ACTION_CODE.WEB_PART_REMOVED_INT_PROVIDERS,
            action: "When internal providers removed",
            detail: `Removed internal provider ${staffName} from ${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`,
          };

    await createAuditLog.mutateAsync({
      appType: "WEB_BACK_OFFICE",
      module: auditLogDetails.module,
      option: auditLogDetails.option,
      actionCode: auditLogDetails.actionCode,
      action: auditLogDetails.action,
      detail: auditLogDetails.detail,
      transactionDate: new Date(),
      accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
      createdBy: userInfo?._id as string,
      targetId: participantId,
      programId: participantResponse?.lastEnrollment?.programId as string,
      orgUnitId: participantResponse?.lastEnrollment?.orgUnitId as string,
    });
  };

  return (
    <Box>
      <ProfileBreadcrumb />

      <ProfileCards
        participantResponse={participantResponse || ({} as ParticipantResponse)}
        isLoadingParticipant={isLoadingParticipant}
        handleCopy={handleCopy}
        handleOpenSupportStaffModal={handleOpenSupportStaffModal}
        handleOpenEditModal={handleOpenEditModal}
        handleOpenDismissModal={handleOpenDismissModal}
        handleOpenManualReimbursementModal={handleOpenManualReimbursementModal}
      />

      {participantResponse && enrollmentBalance && !isLoadingAlerts && (
        <ActionCards
          participantInfo={participantResponse}
          enrollmentBalance={enrollmentBalance}
          alerts={alerts}
          searchJournalResponse={journals}
        />
      )}

      <Grid container mt={0} spacing={2}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {participantResponse?.program?.configuration
            ?.viewBackofficeApptsEnabled && (
            <AppointmentsCard
              participantId={participantId || ""}
              enrollmentId={participantResponse?.lastEnrollment?._id || ""}
              programId={participantResponse?.lastEnrollment?.programId || ""}
              theme={theme}
            />
          )}
        </Grid>
      </Grid>

      <ProviderModulesModal
        roleSubType={RoleSubType.SUPPORT_STAFF}
        participantResponse={participantResponse || ({} as ParticipantResponse)}
        open={isSupportStaffModalOpen}
        onClose={handleCloseSupportStaffModal}
        refetchParticipant={refetchParticipant}
        modules={modules || []}
        isLoadingParticipant={isLoadingParticipant}
        providersList={participantResponse?.lastEnrollment?.supportStaffs || []}
        createAuditLogAddStaff={(staffName, participantId) =>
          createAuditLogAddStaff(
            staffName,
            RoleSubType.SUPPORT_STAFF,
            participantId
          )
        }
        createAuditLogRemoveStaff={(staffName, participantId) =>
          createAuditLogRemoveStaff(
            staffName,
            RoleSubType.SUPPORT_STAFF,
            participantId
          )
        }
      />

      {isManualReimbursementModalOpen ? (
        <ManualReimbursementModal
          participantResponse={
            participantResponse || ({} as ParticipantResponse)
          }
          open={isManualReimbursementModalOpen}
          onClose={handleCloseManualReimbursementModal}
          refetchParticipant={refetchEnrollmentBalance}
        />
      ) : null}

      {isEditModalOpen ? (
        <ClientEditModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          participant={participantResponse || ({} as ParticipantResponse)}
          refetchParticipant={refetchParticipant}
        />
      ) : null}

      {genericAlert.open ? (
        <GenericAlertModal
          onCancel={() =>
            setGenericAlert((prevState) => ({ ...prevState, open: false }))
          }
          onClick={() => {
            genericAlert.onConfirm();
          }}
          title={genericAlert.title}
          description={genericAlert.message}
          submitText="Confirm"
          cancelText="Cancel"
          theme={theme}
        />
      ) : null}

      {isDemoUploadModalOpen ? (
        <CasePlanModal
          open={isDemoUploadModalOpen}
          onClose={handleCloseDemoUploadModal}
        />
      ) : null}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ClientProfile;
